import React, { useEffect } from 'react';
import { Box, IconButton, Select, MenuItem } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { HomePage } from 'app/layout/pages/HomePage';
import { selectUser } from 'oidc/user.selectors';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { getFiscalPeriodDateRange, getCurrentFiscalYearStart, type FiscalPeriodKey } from 'utils/dates/dateExtraction';
import { MyPerformanceDashboardContent } from './MyPerformanceDashboardContent';
import { MyPerformanceFilter } from './WorkdeskFilter/MyPerformanceFilter';
import { myPerformanceReducer, myPerformanceSliceKey, myPerformanceActions } from './MyPerformance.redux';
import { myPerformanceSaga } from './MyPerformance.saga';
import { Concatenate } from 'utils/string/string';
import { defaultValues } from './WorkdeskFilter/MyPerformanceFilterUtils';
import {
  selectMyPerformanceData,
  selectMyPerformanceWidgetLoaded,
  selectMyPerformanceLoadError,
  selectRecruiterOptions,
  selectSelectedEmployeeId,
} from './MyPerformance.selector';
import { getEmptyDashboardData } from './myPerformanceTransformer';

const FISCAL_PERIOD_OPTIONS: { value: FiscalPeriodKey; label: string }[] = [
  { value: '12', label: '12 Months' },
  { value: 'Q1', label: '1st Quarter' },
  { value: 'Q2', label: '2nd Quarter' },
  { value: 'Q3', label: '3rd Quarter' },
  { value: 'Q4', label: '4th Quarter' },
];

const sidePanelItems = [
  { title: 'Quickview', component: <div style={{ minWidth: 236 }} /> },
  { title: 'Advanced Filters', component: <div style={{ minWidth: 236 }} /> },
  { title: 'Tiles', component: <div style={{ minWidth: 236 }} /> },
];

export const MyPerformanceDesk = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector(selectUser) ?? {};
  const employeeId = userInfo?.employeeId ?? 0;
  const [selectedYear, setSelectedYear] = React.useState(() => getCurrentFiscalYearStart());
  const [fiscalPeriod, setFiscalPeriod] = React.useState<FiscalPeriodKey>('12');

  const yearOptions = React.useMemo(() => {
    const current = getCurrentFiscalYearStart();
    return Array.from({ length: 11 }, (_, i) => current - i);
  }, []);
  const formMethods = useForm({ defaultValues, shouldUnregister: false });
  const { handleSubmit, setValue } = formMethods;

  const data = useSelector(selectMyPerformanceData);
  const widgetLoaded = useSelector(selectMyPerformanceWidgetLoaded);
  const loadError = useSelector(selectMyPerformanceLoadError);
  const recruiterOptions = useSelector(selectRecruiterOptions);
  const selectedEmployeeId = useSelector(selectSelectedEmployeeId);

  useInjectReducer({ key: myPerformanceSliceKey, reducer: myPerformanceReducer });
  useInjectSaga({ key: myPerformanceSliceKey, saga: myPerformanceSaga });

  useEffect(() => {
    dispatch(myPerformanceActions.setSelectedEmployeeId(employeeId));
    setSelectedYear(getCurrentFiscalYearStart());
    setFiscalPeriod('12');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (employeeId) {
      dispatch(myPerformanceActions.fetchRecruiterOptionsAction({ userId: employeeId }));
    }
  }, [dispatch, employeeId]);

  useEffect(() => {
    if (employeeId && recruiterOptions.length > 0) {
      const currentUser = recruiterOptions.find((r: { id: number }) => r.id === employeeId);
      if (currentUser) {
        setValue('placementRecruiter', currentUser);
      }
    }
  }, [employeeId, recruiterOptions.length, setValue]);

  useEffect(() => {
    if (recruiterOptions.length > 0 && selectedEmployeeId === null) {
      dispatch(myPerformanceActions.setSelectedEmployeeId(employeeId));
    }
  }, [dispatch, employeeId, recruiterOptions.length, selectedEmployeeId]);

  const effectiveEmployeeId = selectedEmployeeId ?? employeeId;

  useEffect(() => {
    if (effectiveEmployeeId) {
      const { startDate, endDate } = getFiscalPeriodDateRange(fiscalPeriod, selectedYear);
      dispatch(
        myPerformanceActions.fetchMyPerformanceDashboardAction({
          employeeId: effectiveEmployeeId,
          startDate: startDate.slice(0, 10),
          endDate: endDate.slice(0, 10),
        }),
      );
    }
  }, [dispatch, effectiveEmployeeId, fiscalPeriod, selectedYear]);

  const onApplyFilter = (values: Record<string, unknown>) => {
    const recruiter = values.placementRecruiter as { id?: number } | null;
    const newEmployeeId = recruiter?.id ?? null;
    dispatch(myPerformanceActions.setSelectedEmployeeId(newEmployeeId));
  };

  const handleReset = () => {
    if (userInfo?.employeeId) {
      const label = Concatenate([userInfo.firstName ?? '', userInfo.lastName ?? ''], ' ');
      const placementRecruiter = {
        id: userInfo.employeeId,
        name: label,
        value: userInfo.employeeId,
        object: { userId: userInfo.employeeId, firstName: userInfo.firstName, lastName: userInfo.lastName },
      };
      const newFilter = { ...defaultValues, placementRecruiter };
      Object.entries(newFilter).forEach(([key, val]) => setValue(key, val));
      dispatch(myPerformanceActions.setSelectedEmployeeId(userInfo.employeeId));
      setSelectedYear(getCurrentFiscalYearStart());
      setFiscalPeriod('12');
      onApplyFilter(newFilter);
    }
  };

  const handleRefresh = () => {
    handleReset();
    if (userInfo?.employeeId) {
      const { startDate, endDate } = getFiscalPeriodDateRange('12', getCurrentFiscalYearStart());
      dispatch(
        myPerformanceActions.fetchMyPerformanceDashboardAction({
          employeeId: userInfo.employeeId,
          startDate: startDate.slice(0, 10),
          endDate: endDate.slice(0, 10),
        }),
      );
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onApplyFilter)}>
        <HomePage
          title="My Performance"
          titleActions={
            <Box display="flex" alignItems="center" gap={2}>
              <MyPerformanceFilter
                onApplyFilter={onApplyFilter}
                onReset={handleReset}
                hideResetButton
              />
              <Select
                size="small"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                IconComponent={KeyboardArrowDown}
                sx={{
                  minWidth: 100,
                  minHeight: '36.7px',
                  fontSize: 12,
                  '& .MuiSelect-select': { py: 0.5 },
                }}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={fiscalPeriod}
                onChange={(e) => setFiscalPeriod(e.target.value as FiscalPeriodKey)}
                IconComponent={KeyboardArrowDown}
                sx={{
                  minWidth: 130,
                  minHeight: '36.7px',
                  fontSize: 12,
                  '& .MuiSelect-select': { py: 0.5 },
                }}
              >
                {FISCAL_PERIOD_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <IconButton
                color="secondary"
                size="small"
                onClick={handleRefresh}
                sx={{
                  width: '36.7px',
                  height: '36.7px',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                }}
                aria-label="Refresh dashboard"
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          }
          stickIconArray={[3, 2, 1]}
          sidePanelItems={sidePanelItems}
          dataVisualization={
            <MyPerformanceDashboardContent
              data={data ?? getEmptyDashboardData()}
              widgetLoaded={widgetLoaded ?? {}}
              error={loadError}
            />
          }
        />
      </form>
    </FormProvider>
  );
};
